import {
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    Button,
    Icon,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    useColorModeValue,
    useDisclosure,
    useToast
} from '@chakra-ui/react';
import React from 'react';
import { MdOutlineMoreHoriz } from 'react-icons/md';
import { translate } from '../../../i18n';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { useDeleteCoach } from '../hooks/useDeleteCoach';
import ProvisionCoachAccessModal from './ProvisionCoachAccessModal';

type Props = { coachId: string; hasDashboardAccess?: boolean; };
const CoachActionMenu = ({ coachId, hasDashboardAccess }: Props) => {
    const { isOpen: isDialogOpen, onOpen: onDialogOpened, onClose: onDialogClosed } = useDisclosure();
    const { isOpen: isProvisionOpen, onOpen: onProvisionOpened, onClose: onProvisionClosed } = useDisclosure();
    const { selectedOrganization } = useSelectedOrganization();
    const { mutateAsync: deleteAsync, isLoading: isDeleting } = useDeleteCoach(coachId, selectedOrganization?.id);
    const toast = useToast();

    const iconColor = useColorModeValue('brand.500', 'white');
    const bgButton = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');
    const bgHover = useColorModeValue({ bg: 'secondaryGray.400' }, { bg: 'whiteAlpha.50' });
    const bgFocus = useColorModeValue({ bg: 'secondaryGray.300' }, { bg: 'whiteAlpha.100' });

    const cancelRef = React.useRef();

    const handleDelete = async () => {
        try {
            await deleteAsync();
            toast({
                title: translate('coachs.delete_success'),
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            onDialogClosed();
        } catch (error) {
            toast({
                title: translate('coachs.delete_error'),
                description: (error as Error)?.message || 'An error occurred',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    return (
        <>
            <Menu>
                <MenuButton
                    alignItems='center'
                    justifyContent='center'
                    bg={bgButton}
                    _hover={bgHover}
                    _focus={bgFocus}
                    _active={bgFocus}
                    w='37px'
                    h='37px'
                    lineHeight='100%'
                    borderRadius='10px'
                >
                    <Icon as={MdOutlineMoreHoriz} color={iconColor} w='24px' h='24px' />
                </MenuButton>
                <MenuList>
                    <MenuItem onClick={onProvisionOpened} isDisabled={Boolean(hasDashboardAccess)}>
                        {hasDashboardAccess
                            ? translate('coachs.provision_access_has')
                            : translate('coachs.provision_access')}
                    </MenuItem>
                    <MenuItem onClick={onDialogOpened}>{translate('delete')}</MenuItem>
                </MenuList>
            </Menu>

            <ProvisionCoachAccessModal
                coachId={coachId}
                isOpen={isProvisionOpen}
                onClose={onProvisionClosed}
                organizationId={selectedOrganization?.id}
            />

            <AlertDialog
                isOpen={isDialogOpen}
                leastDestructiveRef={cancelRef}
                onClose={onDialogClosed}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            {translate('delete')}
                        </AlertDialogHeader>

                        <AlertDialogBody>
                            {translate('coachs.delete_coach_confirmation_message')}
                        </AlertDialogBody>

                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={onDialogClosed}>
                                {translate('cancel')}
                            </Button>
                            <Button colorScheme="red" onClick={handleDelete} isLoading={isDeleting} ml={3}>
                                {translate('delete')}
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </>
    );
};

export default CoachActionMenu;
